"use client";

import { Notice, StatCard, StatGrid } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  PairedDataInput,
  ResultTable,
  usePairedData,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  formatPRelation,
  formatPValue,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  type SpearmanReport,
  spearmanReport,
} from "@/lib/tool-kit/stats-descriptive/spearman";

/** English strings. A translated content file overrides these through `ui`. */
export const SPEARMAN_CORRELATION_CALCULATOR_STRINGS = {
  entryLabel: "Data entry",
  entryLists: "Separate X and Y lists",
  entryLines: 'Pairs — one "x, y" per line',
  xLabel: "X values",
  yLabel: "Y values",
  xPlaceholder: "1, 2, 2, 3, 4, 5",
  yPlaceholder: "35, 46, 40, 52, 58, 57",
  pairsLabel: "Paired data — one x, y pair per line",
  pairsPlaceholder: "1, 35\n2, 46\n2, 40",
  valuesCount: "one: value|other: values",
  pairsCount: "one: pair read|other: pairs read",
  skippedCount:
    "one: , {count} line without two numbers skipped|other: , {count} lines without two numbers skipped",
  tryExample: "Try an example",
  exampleX: "1, 2, 2, 3, 4, 5, 6, 8, 10, 12",
  exampleY: "35, 46, 40, 52, 58, 57, 64, 66, 69, 68",
  examplePairs:
    "1, 35\n2, 46\n2, 40\n3, 52\n4, 58\n5, 57\n6, 64\n8, 66\n10, 69\n12, 68",
  clear: "Clear",
  mismatch:
    "The two lists differ in length (X: {x}, Y: {y}). Each x needs a matching y.",
  enterPrompt:
    "Enter at least three (x, y) pairs. Each variable needs at least two different values.",
  results: "Results",
  rho: "Spearman's ρ",
  pValue: "p-value (two-tailed)",
  tStatistic: "t",
  degreesOfFreedom: "Degrees of freedom",
  pairsN: "Pairs (n)",
  pearson: "Pearson's r, for comparison",
  strong: "strong",
  moderate: "moderate",
  weak: "weak",
  negligible: "negligible",
  positive: "positive",
  negative: "negative",
  interpretation:
    "ρ = {rho}: a {strength} {direction} monotonic relationship — as one variable's rank goes up, the other's tends to go {way}.",
  wayUp: "up",
  wayDown: "down",
  noRelationship:
    "ρ = {rho}: essentially no monotonic relationship between the two rankings.",
  significant:
    "p {p}, so the relationship is statistically significant at the 5% level.",
  notSignificant:
    "p {p}, so the relationship is not statistically significant at the 5% level.",
  smallSample:
    "With fewer than 10 pairs the t approximation behind this p-value is rough. Check ρ against a table of exact critical values for Spearman's rank correlation.",
  rankTableTitle: "Rank table",
  colX: "x",
  colRankX: "Rank of x",
  colY: "y",
  colRankY: "Rank of y",
  colD: "d",
  colD2: "d²",
  sum: "Sum",
  workingTitle: "How it was calculated",
  rankRule:
    "Each variable is ranked from smallest (1) to largest; tied values share the average of the ranks they cover.",
  rhoLine: "ρ = Pearson's r computed on the two columns of ranks = {rho}",
  shortcutExact:
    "No ties, so the shortcut agrees: ρ = 1 − 6Σd² ÷ (n(n² − 1)) = 1 − 6 × {sumD2} ÷ ({n} × {nn}) = {shortcut}",
  shortcutTies:
    "Your data contain ties, so the shortcut 1 − 6Σd² ÷ (n(n² − 1)) = {shortcut} is only approximate; ρ on the averaged ranks is the exact value.",
  tLine:
    "t = ρ × √((n − 2) ÷ (1 − ρ²)) = {rho} × √({df} ÷ {oneMinus}) = {t}, with {df} degrees of freedom → p {p}",
  perfectLine:
    "|ρ| = 1, a perfect monotonic relationship, so t is infinite and p is 0.",
  pearsonNote:
    "Pearson's r on the raw values is {r}. A clear gap between r and ρ points to outliers or a relationship that is monotonic but curved.",
} as const;

type Strings = typeof SPEARMAN_CORRELATION_CALCULATOR_STRINGS;

function strength(rho: number, strings: Strings): string | null {
  const size = Math.abs(rho);
  if (size >= 0.7) {
    return strings.strong;
  }
  if (size >= 0.4) {
    return strings.moderate;
  }
  if (size >= 0.1) {
    return strings.weak;
  }
  return null;
}

function interpretation(
  report: SpearmanReport,
  num: (value: number) => string,
  strings: Strings
): string {
  const label = strength(report.rho, strings);
  if (label === null) {
    return t(strings.noRelationship, { rho: num(report.rho) });
  }
  const up = report.rho > 0;
  return t(strings.interpretation, {
    rho: num(report.rho),
    strength: label,
    direction: up ? strings.positive : strings.negative,
    way: up ? strings.wayUp : strings.wayDown,
  });
}

function Working({
  report,
  fmt,
  strings,
}: {
  report: SpearmanReport;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const num = (value: number) => formatStat(fmt, value);
  const n = report.n;
  return (
    <WorkingPanel title={strings.workingTitle}>
      <Explanation>{strings.rankRule}</Explanation>
      <Formula>{t(strings.rhoLine, { rho: num(report.rho) })}</Formula>
      <Formula>
        {report.hasTies
          ? t(strings.shortcutTies, { shortcut: num(report.shortcutRho) })
          : t(strings.shortcutExact, {
              sumD2: num(report.sumD2),
              n: fmt(n),
              nn: fmt(n * n - 1),
              shortcut: num(report.shortcutRho),
            })}
      </Formula>
      <Formula>
        {Number.isFinite(report.t)
          ? t(strings.tLine, {
              rho: num(report.rho),
              df: fmt(report.df),
              oneMinus: num(1 - report.rho * report.rho),
              t: num(report.t),
              p: formatPRelation(fmt, report.p),
            })
          : strings.perfectLine}
      </Formula>
      <Explanation>
        {t(strings.pearsonNote, { r: num(report.pearsonR) })}
      </Explanation>
    </WorkingPanel>
  );
}

export default function SpearmanCorrelationCalculatorWidget() {
  const strings = useToolUi(SPEARMAN_CORRELATION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const data = usePairedData();
  const num = (value: number) => formatStat(fmt, value);

  const report = data.mismatched ? null : spearmanReport(data.xs, data.ys);
  const showMismatch =
    data.mismatched && data.listXCount > 0 && data.listYCount > 0;
  const count = (n: number) => (
    <>
      {fmt(n)}
      {tPlural(strings.valuesCount, locale, n)}
    </>
  );

  return (
    <>
      <section className="space-y-4">
        <PairedDataInput
          data={data}
          idPrefix="sp"
          labels={{
            mode: strings.entryLabel,
            lists: strings.entryLists,
            lines: strings.entryLines,
            x: strings.xLabel,
            y: strings.yLabel,
            xPlaceholder: strings.xPlaceholder,
            yPlaceholder: strings.yPlaceholder,
            pairs: strings.pairsLabel,
            pairsPlaceholder: strings.pairsPlaceholder,
          }}
          pairsHint={
            <>
              {fmt(data.pairCount)}
              {tPlural(strings.pairsCount, locale, data.pairCount)}
              {data.skippedLines > 0
                ? tPlural(strings.skippedCount, locale, data.skippedLines, {
                    count: fmt(data.skippedLines),
                  })
                : null}
            </>
          }
          xHint={count(data.listXCount)}
          yHint={count(data.listYCount)}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              data.fill({
                x: strings.exampleX,
                y: strings.exampleY,
                pairs: strings.examplePairs,
              })
            }
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={data.clear}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {showMismatch ? (
        <Notice>
          {t(strings.mismatch, {
            x: fmt(data.listXCount),
            y: fmt(data.listYCount),
          })}
        </Notice>
      ) : null}

      {report ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite">
            <StatGrid>
              <StatCard emphasis label={strings.rho} value={num(report.rho)} />
              <StatCard
                label={strings.pValue}
                value={formatPValue(fmt, report.p)}
              />
              <StatCard label={strings.tStatistic} value={num(report.t)} />
              <StatCard
                label={strings.degreesOfFreedom}
                value={fmt(report.df)}
              />
              <StatCard label={strings.pairsN} value={fmt(report.n)} />
              <StatCard label={strings.pearson} value={num(report.pearsonR)} />
            </StatGrid>
          </div>
          <div className="space-y-2">
            <Explanation>{interpretation(report, num, strings)}</Explanation>
            <Explanation>
              {t(
                report.p < 0.05 ? strings.significant : strings.notSignificant,
                {
                  p: formatPRelation(fmt, report.p),
                }
              )}
            </Explanation>
            {report.n < 10 ? <Notice>{strings.smallSample}</Notice> : null}
          </div>
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.rankTableTitle}</h3>
            <ResultTable
              footer={[strings.sum, "", "", "", "", num(report.sumD2)]}
              head={[
                strings.colX,
                strings.colRankX,
                strings.colY,
                strings.colRankY,
                strings.colD,
                strings.colD2,
              ]}
              rows={report.rows.map((row) => [
                num(row.x),
                num(row.rankX),
                num(row.y),
                num(row.rankY),
                num(row.d),
                num(row.d2),
              ])}
            />
          </div>
          <Working fmt={fmt} report={report} strings={strings} />
        </section>
      ) : null}
      {report || showMismatch ? null : <Notice>{strings.enterPrompt}</Notice>}
    </>
  );
}
